// src/Exchange/ExchangeAdmin.js
import * as React from "react";
import { Title, useNotify, usePermissions } from "react-admin";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Divider,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Stack,
    Chip,
    CircularProgress,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Breadcrumbs,
    Link,
    Menu,
    MenuItem,
    Grid,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderIcon from "@mui/icons-material/Folder";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import EditIcon from "@mui/icons-material/Edit";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HomeIcon from "@mui/icons-material/Home";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import axiosInstance from "../Tools/Interceptors"; // adapte si ton chemin diffère

function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return "-";
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
    const value = bytes / Math.pow(1024, i);
    return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${sizes[i]}`;
}

function formatDate(d) {
    try {
        return new Date(d).toLocaleString();
    } catch {
        return "-";
    }
}

function extractHydraMembers(data) {
    // API Platform / Hydra: { "hydra:member": [...] }
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data["hydra:member"] || [];
}

export default function ExchangeAdmin() {
    const notify = useNotify();
    const { permissions } = usePermissions();
    const isAdmin = Array.isArray(permissions) && permissions.includes('ROLE_ADMIN');
    const inputRef = React.useRef(null);

    const [items, setItems] = React.useState([]);
    const [folders, setFolders] = React.useState([]);
    const [currentFolderId, setCurrentFolderId] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);

    // Dialog states
    const [createFolderOpen, setCreateFolderOpen] = React.useState(false);
    const [newFolderName, setNewFolderName] = React.useState("");
    const [renameFolderOpen, setRenameFolderOpen] = React.useState(false);
    const [renameFolderData, setRenameFolderData] = React.useState(null);
    const [renameFileOpen, setRenameFileOpen] = React.useState(false);
    const [renameFileData, setRenameFileData] = React.useState(null);
    const [moveFileOpen, setMoveFileOpen] = React.useState(false);
    const [moveFileData, setMoveFileData] = React.useState(null);

    // Menu states
    const [folderMenuAnchor, setFolderMenuAnchor] = React.useState(null);
    const [folderMenuData, setFolderMenuData] = React.useState(null);

    const fetchFolders = React.useCallback(async () => {
        try {
            const res = await axiosInstance.get("/folders");
            const all = extractHydraMembers(res.data);
            setFolders(all.filter(f => f.space === "shared"));
        } catch (e) {
            notify("Erreur chargement des dossiers", { type: "warning" });
        }
    }, [notify]);

    const fetchFiles = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get("/exchange_files");
            const all = extractHydraMembers(res.data);
            setItems(all.filter(f => f.space === "shared"));
        } catch (e) {
            notify("Erreur chargement des fichiers", { type: "warning" });
            // console.error(e);
        } finally {
            setLoading(false);
        }
    }, [notify]);

    React.useEffect(() => {
        fetchFolders();
        fetchFiles();
    }, [fetchFolders, fetchFiles]);

    const onPickFiles = () => inputRef.current?.click();

    const uploadOne = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("space", "shared");
        if (currentFolderId) {
            fd.append("folderId", currentFolderId);
        }

        // IMPORTANT: ne pas forcer Content-Type; axios le met avec boundary automatiquement
        const res = await axiosInstance.post("/exchange_files", fd);
        return res.data;
    };

    const addFiles = async (fileList) => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;

        setUploading(true);
        try {
            // Upload 1 par 1 (simple et compatible avec le controller actuel)
            for (const f of incoming) {
                await uploadOne(f);
            }
            notify(`${incoming.length} fichier(s) uploadé(s)`, { type: "info" });
            await fetchFiles();
        } catch (e) {
            notify("Upload échoué", { type: "warning" });
            // console.error(e);
        } finally {
            setUploading(false);
        }
    };

    const onInputChange = async (e) => {
        await addFiles(e.target.files);
        e.target.value = "";
    };

    const onDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.files?.length) await addFiles(e.dataTransfer.files);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const downloadFile = async (item) => {
        try {
            const response = await axiosInstance.get(`/exchange_files/${item.id}/download`, {
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", item.originalName || `fichier_${item.id}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            notify("Téléchargement échoué", { type: "warning" });
        }
    };

    const deleteFile = async (item) => {
        try {
            // item['@id'] est parfait pour DELETE
            const iri = item["@id"] || `/exchange_files/${item.id}`;
            await axiosInstance.delete(iri);
            notify("Fichier supprimé", { type: "info" });
            await fetchFiles();
        } catch (e) {
            notify("Suppression échouée", { type: "warning" });
            // console.error(e);
        }
    };

    // Folder operations
    const createFolder = async () => {
        if (!newFolderName.trim()) return;
        try {
            const payload = { name: newFolderName, space: "shared" };
            if (currentFolderId) {
                payload.parentId = currentFolderId;
            }
            console.log("Creating folder with payload:", payload, "currentFolderId:", currentFolderId);
            await axiosInstance.post("/folders", payload);
            notify("Dossier créé", { type: "info" });
            setNewFolderName("");
            setCreateFolderOpen(false);
            await fetchFolders();
        } catch (e) {
            console.error("Create folder error:", e);
            notify("Création échouée", { type: "warning" });
        }
    };

    const renameFolder = async () => {
        if (!renameFolderData || !renameFolderData.name.trim()) return;
        try {
            await axiosInstance.patch(`/folders/${renameFolderData.id}`, {
                name: renameFolderData.name,
            }, {
                headers: { "Content-Type": "application/merge-patch+json" },
            });
            notify("Dossier renommé", { type: "info" });
            setRenameFolderOpen(false);
            setRenameFolderData(null);
            await fetchFolders();
        } catch (e) {
            notify("Renommage échoué", { type: "warning" });
        }
    };

    const deleteFolder = async (folder) => {
        if (!window.confirm(`Supprimer le dossier "${folder.name}" ?`)) return;
        try {
            await axiosInstance.delete(`/folders/${folder.id}`);
            notify("Dossier supprimé", { type: "info" });
            await fetchFolders();
            await fetchFiles();
        } catch (e) {
            notify("Suppression échouée", { type: "warning" });
        }
    };

    const renameFile = async () => {
        if (!renameFileData || !renameFileData.name.trim()) return;
        try {
            await axiosInstance.patch(`/exchange_files/${renameFileData.id}`, {
                originalName: renameFileData.name,
            }, {
                headers: { "Content-Type": "application/merge-patch+json" },
            });
            notify("Fichier renommé", { type: "info" });
            setRenameFileOpen(false);
            setRenameFileData(null);
            await fetchFiles();
        } catch (e) {
            notify("Renommage échoué", { type: "warning" });
        }
    };

    const moveFile = async () => {
        if (!moveFileData) return;
        try {
            const body = (moveFileData.targetFolderId !== null && moveFileData.targetFolderId !== undefined)
                ? { folder: `/folders/${moveFileData.targetFolderId}` }
                : { folder: null };
            await axiosInstance.patch(`/exchange_files/${moveFileData.file.id}`, body, {
                headers: { "Content-Type": "application/merge-patch+json" },
            });
            notify("Fichier déplacé", { type: "info" });
            setMoveFileOpen(false);
            setMoveFileData(null);
            await fetchFiles();
        } catch (e) {
            notify("Déplacement échoué", { type: "warning" });
        }
    };

    // Get folders in current directory
    const currentFolders = React.useMemo(() => {
        return folders.filter((f) => {
            const parentId = f.parent?.id || null;
            return parentId === currentFolderId;
        });
    }, [folders, currentFolderId]);

    // Get files in current directory
    const currentFiles = React.useMemo(() => {
        return items.filter((file) => {
            const folderId = file.folder?.id || null;
            return folderId === currentFolderId;
        });
    }, [items, currentFolderId]);

    // Build breadcrumbs
    const breadcrumbs = React.useMemo(() => {
        const result = [];
        let fId = currentFolderId;
        while (fId) {
            const folder = folders.find((f) => f.id === fId);
            if (!folder) break;
            result.unshift(folder);
            fId = folder.parent?.id || null;
        }
        return result;
    }, [currentFolderId, folders]);

    return (
        <Box sx={{ p: 2, height: "100%" }}>
            <Title title="Espace de stockage partagé" />

            {/* Header with breadcrumbs */}
            <Box sx={{ mb: 2, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    Espace de stockage partagé
                </Typography>
                <Breadcrumbs>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => setCurrentFolderId(null)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            textDecoration: "none",
                            color: "text.primary",
                            "&:hover": { textDecoration: "underline" },
                        }}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                        Racine
                    </Link>
                    {breadcrumbs.map((folder) => (
                        <Link
                            key={folder.id}
                            component="button"
                            variant="body2"
                            onClick={() => setCurrentFolderId(folder.id)}
                            sx={{
                                textDecoration: "none",
                                color: "text.primary",
                                "&:hover": { textDecoration: "underline" }
                            }}
                        >
                            {folder.name}
                        </Link>
                    ))}
                </Breadcrumbs>
            </Box>

            <Stack spacing={2}>
                {/* Upload zone — admin only */}
                {isAdmin && <Box
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    sx={{
                        border: "2px dashed",
                        borderColor: uploading ? "primary.main" : "divider",
                        borderRadius: 1,
                        p: 3,
                        bgcolor: "background.paper",
                        transition: "all 0.2s ease",
                        "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "action.hover"
                        }
                    }}
                >
                    <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <CloudUploadIcon sx={{ fontSize: 32, color: "text.secondary" }} />
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    Glissez-déposez vos fichiers ici
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Formats acceptés: tous types de fichiers
                                </Typography>
                            </Box>
                        </Stack>

                        <input
                            ref={inputRef}
                            type="file"
                            multiple
                            hidden
                            onChange={onInputChange}
                        />

                        <Stack direction="row" spacing={1} alignItems="center">
                            {uploading && <CircularProgress size={20} />}
                            <Button
                                variant="contained"
                                onClick={onPickFiles}
                                startIcon={<UploadFileIcon />}
                                disabled={uploading}
                            >
                                {uploading ? "Upload..." : "Parcourir"}
                            </Button>
                        </Stack>
                    </Stack>
                </Box>}

                {/* Files and folders list */}
                <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Fichiers et dossiers
                                </Typography>
                                <Chip
                                    size="small"
                                    label={`${currentFolders.length + currentFiles.length} éléments`}
                                    variant="outlined"
                                />
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {loading && <CircularProgress size={18} />}
                                {isAdmin && (
                                    <Button
                                        size="small"
                                        startIcon={<CreateNewFolderIcon />}
                                        onClick={() => setCreateFolderOpen(true)}
                                    >
                                        Nouveau dossier
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </Box>

                    <Box sx={{ p: 1 }}>
                        {currentFolders.length === 0 && currentFiles.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 8 }}>
                                <FolderOpenIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2, opacity: 0.3 }} />
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    Aucun élément
                                </Typography>
                                <Typography variant="body2" color="text.disabled">
                                    {isAdmin ? "Créez un dossier ou téléchargez des fichiers pour commencer" : "Aucun fichier disponible"}
                                </Typography>
                            </Box>
                        ) : (
                            <List dense>
                                {/* Folders */}
                                {currentFolders.map((folder) => (
                                    <ListItem
                                        key={folder.id}
                                        button
                                        onClick={() => setCurrentFolderId(folder.id)}
                                        sx={{
                                            borderRadius: 1,
                                            mb: 0.5,
                                            "&:hover": { bgcolor: "action.hover" }
                                        }}
                                        secondaryAction={isAdmin ? (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFolderMenuAnchor(e.currentTarget);
                                                    setFolderMenuData(folder);
                                                }}
                                            >
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined}
                                    >
                                        <FolderIcon sx={{ mr: 2, color: "warning.main" }} />
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {folder.name}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(folder.createdAt)}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}

                                {/* Files */}
                                {currentFiles.map((f) => (
                                    <ListItem
                                        key={f["@id"] || f.id}
                                        sx={{
                                            borderRadius: 1,
                                            mb: 0.5,
                                            "&:hover": { bgcolor: "action.hover" }
                                        }}
                                        secondaryAction={
                                            <Stack direction="row" spacing={0.5}>
                                                {isAdmin && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setMoveFileData({ file: f, targetFolderId: currentFolderId });
                                                            setMoveFileOpen(true);
                                                        }}
                                                        title="Déplacer"
                                                    >
                                                        <DriveFileMoveIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => downloadFile(f)}
                                                    title="Télécharger"
                                                >
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                                {isAdmin && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => deleteFile(f)}
                                                        title="Supprimer"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        }
                                    >
                                        <InsertDriveFileIcon sx={{ mr: 2, color: "text.secondary" }} />
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {f.originalName}
                                                    </Typography>
                                                </Stack>
                                            }
                                            secondary={
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(f.createdAt)}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                </Box>

                {/* Folder context menu */}
                <Menu
                    anchorEl={folderMenuAnchor}
                    open={Boolean(folderMenuAnchor)}
                    onClose={() => {
                        setFolderMenuAnchor(null);
                        setFolderMenuData(null);
                    }}
                >
                    <MenuItem
                        onClick={() => {
                            setRenameFolderData({ ...folderMenuData });
                            setRenameFolderOpen(true);
                            setFolderMenuAnchor(null);
                        }}
                    >
                        <EditIcon sx={{ mr: 1 }} fontSize="small" />
                        Renommer
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            deleteFolder(folderMenuData);
                            setFolderMenuAnchor(null);
                            setFolderMenuData(null);
                        }}
                    >
                        <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                        Supprimer
                    </MenuItem>
                </Menu>

                {/* Create folder dialog */}
                <Dialog open={createFolderOpen} onClose={() => setCreateFolderOpen(false)}>
                    <DialogTitle>Créer un dossier</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Nom du dossier"
                            fullWidth
                            size="small"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === "Enter") createFolder();
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCreateFolderOpen(false)} size="small">
                            Annuler
                        </Button>
                        <Button onClick={createFolder} variant="contained" size="small">
                            Créer
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Rename file dialog */}
                <Dialog open={renameFileOpen} onClose={() => setRenameFileOpen(false)}>
                    <DialogTitle>Renommer le fichier</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Nouveau nom"
                            fullWidth
                            size="small"
                            value={renameFileData?.name || ""}
                            onChange={(e) =>
                                setRenameFileData((prev) => ({ ...prev, name: e.target.value }))
                            }
                            onKeyPress={(e) => {
                                if (e.key === "Enter") renameFile();
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRenameFileOpen(false)} size="small">
                            Annuler
                        </Button>
                        <Button onClick={renameFile} variant="contained" size="small">
                            Renommer
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Rename folder dialog */}
                <Dialog open={renameFolderOpen} onClose={() => setRenameFolderOpen(false)}>
                    <DialogTitle>Renommer le dossier</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Nouveau nom"
                            fullWidth
                            size="small"
                            value={renameFolderData?.name || ""}
                            onChange={(e) =>
                                setRenameFolderData((prev) => ({ ...prev, name: e.target.value }))
                            }
                            onKeyPress={(e) => {
                                if (e.key === "Enter") renameFolder();
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRenameFolderOpen(false)} size="small">
                            Annuler
                        </Button>
                        <Button onClick={renameFolder} variant="contained" size="small">
                            Renommer
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Move file dialog */}
                <Dialog open={moveFileOpen} onClose={() => setMoveFileOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Déplacer le fichier</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            <strong>{moveFileData?.file?.originalName}</strong>
                        </Typography>
                        <List dense sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                            <ListItem
                                button
                                selected={moveFileData?.targetFolderId === null}
                                onClick={() =>
                                    setMoveFileData((prev) => ({ ...prev, targetFolderId: null }))
                                }
                            >
                                <HomeIcon sx={{ mr: 1.5 }} fontSize="small" />
                                <ListItemText primary="Racine" />
                            </ListItem>
                            {folders.map((folder) => (
                                <ListItem
                                    key={folder.id}
                                    button
                                    selected={moveFileData?.targetFolderId === folder.id}
                                    onClick={() =>
                                        setMoveFileData((prev) => ({ ...prev, targetFolderId: folder.id }))
                                    }
                                >
                                    <FolderIcon sx={{ mr: 1.5 }} fontSize="small" />
                                    <ListItemText primary={folder.name} />
                                </ListItem>
                            ))}
                        </List>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setMoveFileOpen(false)} size="small">
                            Annuler
                        </Button>
                        <Button onClick={moveFile} variant="contained" size="small">
                            Déplacer
                        </Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        </Box>
    );
}
